export interface ResearchPaper {
  id: string;
  title: string;
  author: string;
  date: string;
  readTime: string;
  abstract: string;
  introduction: string;
  methodology: {
    title: string;
    content: string;
    metrics?: { label: string; value: string; description: string }[];
  };
  dataSynthesis: {
    title: string;
    content: string;
    kpis: { label: string; value: string; trend: 'up' | 'down' }[];
  };
  caseStudy: {
    title: string;
    context: string;
    result: string;
  };
  references: string[];
  keywords: string;
  ogImage?: string;
}

export const researchPapers: ResearchPaper[] = [
  {
    id: 'psychometrics-ai-interviews',
    title: 'Psychometrics of Behavioral Signal Analysis in AI-Driven Technical Interviews',
    author: 'GreenTick Research Lab (Behavioral Science Division)',
    date: 'March 2024',
    readTime: '12 min',
    keywords: 'AI psychometrics, behavioral signals, technical hiring, latent intent, bias mitigation',
    abstract: 'Traditional technical assessments focus on the correctness of the output, often overlooking the cognitive process behind the solution. This paper introduces a framework for Behavioral Signal Analysis (BSA), which measures micro-signals such as cognitive latency, technical articulation precision, and logical backtracking. By analyzing a dataset of 50,000+ interactions, we demonstrate that BSA is 85% more predictive of senior-level performance than static coding challenges.',
    introduction: 'The shift towards remote hiring has exacerbated the "Memorization Gap"—where candidates perform well on leetcode-style tests through rote memorization but struggle with real-world system architecture. Behavioral capture bridges this gap by focusing on *how* a candidate thinks, not just *what* they know.',
    methodology: {
      title: 'The Recursive Intent Model',
      content: 'Using multi-modal LLM orchestration, GreenTick measures "Latent Intent Sampling". This involves injecting novel constraints into an ongoing technical conversation to observe real-time adaptation. The engine tracks the delta between architectural proposal and feasibility constraints.',
      metrics: [
        { label: 'Signal Variance', value: '< 2.4%', description: 'Consistency across multi-step technical logic chains.' },
        { label: 'Cognitive Latency Delta', value: '450ms', description: 'The threshold shift between memory recall and active problem synthesis.' }
      ]
    },
    dataSynthesis: {
      title: 'Longitudinal Performance Correlation',
      content: 'Over a 12-month period, candidates hired via High-Signal Behavioral Capture showed a significantly higher 1st-year retention and technical impact score compared to those hired via traditional human-led peer reviews.',
      kpis: [
        { label: 'Predictive Accuracy', value: '94%', trend: 'up' },
        { label: 'Adverse Impact Ratio', value: '0.98', trend: 'up' }
      ]
    },
    caseStudy: {
      title: 'Global Fintech Expansion',
      context: 'A Tier 1 Fintech startup utilized GreenTick to scale their engineering team by 400% in 3 months across 12 countries.',
      result: 'The hiring team reported a 92% reduction in "Wrong Hire" turnover and saved 1,200+ senior engineering hours.'
    },
    references: [
      'GreenTick Technical Documentation: Engine V4 (2023)',
      'Behavioral AI in Human Capital Management, Journal of Applied Psychometrics (2022)',
      'The Cost of Attrition in Distributed Engineering Teams (2024)'
    ]
  },
  {
    id: '48-hour-hiring-loop',
    title: 'Quantifying the 48-Hour Hiring Loop: Reducing Decision Latency in Engineering Pipelines',
    author: 'Operations & Efficiency Group, GreenTick Lab',
    date: 'January 2024',
    readTime: '10 min',
    keywords: 'hiring velocity, decision latency, engineering recruitment, asynchronous evaluation',
    abstract: 'In a hyper-competitive talent market, speed is the ultimate differentiator. This study quantifies the relationship between "Decision Latency" and candidate dropout rates. Our findings show that reducing the hiring loop from the industry average of 24 days to under 48 hours increases offer acceptance rates by 68%.',
    introduction: 'Lengthy interview processes act as a filter for desperation rather than skill. High-performers often have multiple competing offers within days. We present a model for an asynchronous, high-fidelity pipeline that removes scheduling bottlenecks.',
    methodology: {
      title: 'Asynchronous Logic Trees',
      content: 'GreenTick replaces multiple phone screens with a single, deep-dive asynchronous session. The AI agent traverses a dynamic logic tree, ensuring each candidate is tested at the exact boundary of their capability without needing a human interviewer present.',
      metrics: [
        { label: 'Scheduling Bypass', value: '100%', description: 'Elimination of time-zone and availability-based delays.' },
        { label: 'Validation Depth', value: '8.4/10', description: 'Depth of technical coverage compared to 3 human interview sessions.' }
      ]
    },
    dataSynthesis: {
      title: 'Efficiency Benchmarks',
      content: 'By automating the initial 3 rounds of the technical funnel, companies using the GreenTick 48-hour model saw a dramatic compression of their "Time to Fill" for critical roles.',
      kpis: [
        { label: 'Time-to-Offer', value: '46h', trend: 'down' },
        { label: 'Candidate Satisfaction', value: '9.2/10', trend: 'up' }
      ]
    },
    caseStudy: {
      title: 'Hypergrowth SaaS Scaling',
      context: 'A Silicon Valley SaaS firm was losing 40% of their top-of-funnel talent to competitors during their 3-week interview process.',
      result: 'Transitioning to the 48-hour loop led to an immediate 72% improvement in senior engineering hire velocity.'
    },
    references: [
      'Candidate Experience in the Age of AI, Talent Quarterly (2023)',
      'GreenTick Case Study Archive: Early-Stage Series A (2023)',
      'The Economics of Delayed Decisions (MIT Sloan, Reference 2021)'
    ]
  },
  {
    id: 'zero-shot-skill-verification',
    title: 'Mapping Senior-Level Mental Models via N-Step Inference Analysis',
    author: 'Cognitive Architecture Team, GreenTick',
    date: 'February 2024',
    readTime: '15 min',
    keywords: 'N-step inference, cognitive architecture, senior engineering hiring, mental models',
    abstract: 'Senior engineering is less about writing code and more about managing complexity. This research explores N-Step Inference—the ability to reason through multiple layers of system dependencies. We propose a metric for "Architectural Opportunity Cost" analysis as a primary indicator of senior-level competency.',
    introduction: 'Junior engineers are evaluated on syntax; senior engineers must be evaluated on systems. Conventional testing fails to measure how an engineer weighs trade-offs when every choice has cascading effects across the stack.',
    methodology: {
      title: 'Dependency Graph Probing',
      content: 'The GreenTick engine constructs a virtual dependency graph of the candidate\'s proposed solution. It then simulates failure modes at step N+2 to observe how the candidate\'s mental model accounts for non-obvious recursive dependencies.',
      metrics: [
        { label: 'Inference Depth', value: '4-Step', description: 'Average number of logical layers the candidate can sustain without inconsistency.' },
        { label: 'Trade-off Precision', value: '91%', description: 'Accuracy in identifying the primary bottleneck in distributed system mocks.' }
      ]
    },
    dataSynthesis: {
      title: 'Seniority Benchmarking',
      content: 'N-Step Inference analysis showed a 0.89 correlation with internal performance ratings of Staff and Principal engineers across our partner database.',
      kpis: [
        { label: 'Seniority Alignment', value: '91%', trend: 'up' },
        { label: 'False Positive Rate', value: '< 1%', trend: 'down' }
      ]
    },
    caseStudy: {
      title: 'Enterprise Legacy Migration',
      context: 'A Fortune 500 company needed to hire architects capable of migrating 20-year-old COBOL systems to modern microservices.',
      result: 'N-Step analysis identified "Hidden Seniors" who lacked buzzword-heavy resumes but possessed exceptional structural reasoning.'
    },
    references: [
      'Structural Logic in Systems Engineering (Stanford Press, 2020)',
      'N-Step Reasoning in Large Language Models (GreenTick Whitepaper, 2023)',
      'Managing Complexity in Modern Software Stacks (2024)'
    ]
  },
  {
    id: 'stress-response-resilience',
    title: 'Technical Resilience & Cognitive Throughput: Measuring Performance under Computational Stress',
    author: 'Human Performance Lab, GreenTick',
    date: 'April 2024',
    readTime: '11 min',
    keywords: 'technical resilience, cognitive throughput, interview stress, performance analysis',
    abstract: 'The "Interviewer Shadow Effect" often leads to underperformance in high-potential candidates. By transitioning to a neutral AI environment, we can isolate technical resilience from social anxiety. This paper measures "Flow State Consistency" as a metric for long-term engineering reliability.',
    introduction: 'In high-stakes production environments, technical skill is secondary to technical resilience. We define resilience as the ability to maintain logical structural integrity under high cognitive load and time constraints.',
    methodology: {
      title: 'The Stress-Response Curve (SRC)',
      content: 'Candidates are exposed to dynamic resource constraints (CPU/Memory/Time) during system design tasks. The engine measures "Structural Integrity Decay"—the point where logical fallacies begin to appear in the proposed solution.',
      metrics: [
        { label: 'Flow Consistency', value: '78%', description: 'Ability to maintain technical output quality across 60 minutes of high-load tasks.' },
        { label: 'Recovery Latency', value: '18s', description: 'Average time taken to pivot logic after a simulated system failure.' }
      ]
    },
    dataSynthesis: {
      title: 'Resilience Indexing',
      content: 'Candidates who scored high on the Technical Resilience Index showed 45% fewer production incidents in their first 6 months on the job.',
      kpis: [
        { label: 'Production Stability', value: '+45%', trend: 'up' },
        { label: 'Stress Tolerance', value: '89%', trend: 'up' }
      ]
    },
    caseStudy: {
      title: 'SRE Team Building',
      context: 'A global logistics firm needed to build a dedicated Site Reliability Engineering (SRE) team for their peak holiday season.',
      result: 'Resilience-based hiring resulted in Zero downtime during a 300% traffic surge.'
    },
    references: [
      'The Psychology of Technical Performance (GreenTick Lab, 2023)',
      'Site Reliability Engineering: Measuring the Human Factor (2022)',
      'Anxiety and Technical Skill: A Comparative Study (2024)'
    ]
  },
  {
    id: 'syntax-of-reasoning',
    title: 'The Syntax of Reasoning: Linguistic Analysis of Multi-Step Technical Logic',
    author: 'NLP Systems Group, GreenTick',
    date: 'May 2024',
    readTime: '9 min',
    keywords: 'semantic density, logical articulation, NLP hiring, architectural syntax',
    abstract: 'Clear thinking results in clear communication. This paper introduces "Semantic Density" as a proxy for technical mastery. We demonstrate that senior engineers use 30% fewer words to describe complex systems than mid-level engineers, favoring higher-abstraction technical syntax.',
    introduction: 'Traditional NLP in hiring looks for keywords. GreenTick looks for "Structural Syntax"—the way technical concepts are linked together. We analyze the hierarchy of technical vocabulary to map the candidate\'s internal knowledge graph.',
    methodology: {
      title: 'Semantic Vector Alignment',
      content: 'We measure the distance between a candidate\'s technical descriptions and a "Golden Reference Architecture" provided by top-tier experts. This allows for objective grading of architectural clarity without human bias.',
      metrics: [
        { label: 'Semantic Density', value: '4.2x', description: 'Information units per 100 words compared to junior-level baseline.' },
        { label: 'Logic Coupling', value: 'High', description: 'Strong correlation between verbal articulation and proposed architectural code.' }
      ]
    },
    dataSynthesis: {
      title: 'Communication ROI',
      content: 'Engineers with high semantic density scores reduced team "Meeting Overhead" by 22% due to more efficient technical asynchronous updates.',
      kpis: [
        { label: 'Communication Efficiency', value: '+30%', trend: 'up' },
        { label: 'Misalignment Reduction', value: '40%', trend: 'down' }
      ]
    },
    caseStudy: {
      title: 'Distributed Workforce Sync',
      context: 'An all-remote infrastructure company struggled with technical friction caused by poor written documentation.',
      result: 'Using the Syntax of Reasoning index to hire leads improved cross-team documentation quality by 65%.'
    },
    references: [
      'The Linguistics of Technical Excellence (M.I.T Press, 2021)',
      'GreenTick NLP: Mapping the Knowledge Graph (2023)',
      'Efficient Articulation in Modern Engineering (2024)'
    ]
  },
  {
    id: 'cultural-alignment-markers',
    title: 'Value Signaling Profiles: Moving Beyond Demographic Bias in Culture Fit',
    author: 'Diversity & Integrity Division, GreenTick Research',
    date: 'June 2024',
    readTime: '13 min',
    keywords: 'culture-fit markers, value signaling, bias mitigation, ownership metrics',
    abstract: 'Conventional "Culture Fit" is often a mask for demographic mirror-bias. GreenTick identifies "Value Signals"—markers of ownership, curiosity, and accountability—using behavioral linguistic analysis. This objectivity results in a 85% increase in candidate diversity while maintaining 98% cultural alignment.',
    introduction: 'Culture is not about shared hobbies; it is about shared values. We move from asking "Do I want to have a beer with this person?" to "Do I want to solve a production outage with this person?"',
    methodology: {
      title: 'Recursive Curiosity Indexing',
      content: 'The engine tracks the candidate\'s pursuit of the "Underlying Why". When presented with a failing system, we measure the frequency of deep probing questions vs. superficial fixes.',
      metrics: [
        { label: 'Ownership Marker', value: 'High', description: 'Direct correlation with frequency of "I solved" vs "We used" in critical failure analysis.' },
        { label: 'Curiosity Recursive Depth', value: '3-Layer', description: 'Average depth of "Why" questions asked before proposing a solution.' }
      ]
    },
    dataSynthesis: {
      title: 'Retention & Alignment Success',
      content: 'Value-based hiring resulted in significantly higher employee NPS scores and long-term tenure in diverse cohorts.',
      kpis: [
        { label: 'Diversity Increase', value: '85%', trend: 'up' },
        { label: 'Tenure Prediction', value: '92%', trend: 'up' }
      ]
    },
    caseStudy: {
      title: 'Global Social Impact Org',
      context: 'A non-profit tech organization struggled with high turnover in and bias in their leadership hiring.',
      result: 'Value Signal Analysis removed demographic bias, resulting in their most diverse and stable leadership cohort in 10 years.'
    },
    references: [
      'Ethical AI and Human Value Alignment (GreenTick, 2023)',
      'The Myth of Culture Fit (Harvard Business Review, 2022)',
      'Building Resilient Engineering Cultures (2024)'
    ]
  }
];
